/**
 * Plays and likes for every game, shown on the tiles and the game pages.
 * Totals come from GET /api/stats (worker/index.ts, cached 5 minutes); plays are counted by the visit
 * beacon (lib/analytics.ts). Which games this browser has liked is kept only in localStorage, so a
 * like needs no account and nothing about the player is sent: just the game id and like or un-like.
 */
import { useSyncExternalStore } from 'react';
import { readJson, writeJson } from './storage';

const LIKED_KEY = 'sf:liked';

interface Totals {
  plays: Record<string, number>;
  likes: Record<string, number>;
}

let totals: Totals = { plays: {}, likes: {} };
let liked = new Set<string>(typeof window === 'undefined' ? [] : readJson<string[]>(LIKED_KEY, []));
let requested = false;
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version++;
  for (const l of listeners) l();
}

function load(): void {
  if (requested || typeof fetch !== 'function') return;
  requested = true;
  fetch('/api/stats')
    .then((r) => (r.ok ? r.json() : null))
    .then((d: Partial<Totals> | null) => {
      if (!d || typeof d !== 'object') return;
      const clean = (m: unknown) =>
        Object.fromEntries(Object.entries(m && typeof m === 'object' ? m : {}).filter(([, v]) => typeof v === 'number' && v >= 0)) as Record<string, number>;
      totals = { plays: clean(d.plays), likes: clean(d.likes) };
      changed();
    })
    .catch(() => {});
}

function subscribe(fn: () => void): () => void {
  load();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-renders when totals arrive or a like changes. */
export function useGameStats(id: string): { plays: number; likes: number; liked: boolean } {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => 0,
  );
  const mine = liked.has(id);
  // The cached server total may not include this browser's like yet; count it at least once.
  const likes = Math.max(totals.likes[id] ?? 0, mine ? 1 : 0);
  return { plays: totals.plays[id] ?? 0, likes, liked: mine };
}

/** Likes or un-likes a game: updates the count straight away, then tells the server. */
export function toggleLike(id: string): void {
  const on = !liked.has(id);
  liked = new Set(liked);
  if (on) liked.add(id);
  else liked.delete(id);
  writeJson(LIKED_KEY, [...liked]);
  totals = { ...totals, likes: { ...totals.likes, [id]: Math.max(0, (totals.likes[id] ?? 0) + (on ? 1 : -1)) } };
  changed();
  fetch('/api/like', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ game: id, on }), keepalive: true }).catch(() => {});
}

/** 1234 → "1.2K", 1500000 → "1.5M". */
export function shortCount(n: number): string {
  if (n < 1000) return String(n);
  const [d, s] = n < 1e6 ? [1e3, 'K'] : [1e6, 'M'];
  const v = n / d;
  return `${v < 10 ? Math.floor(v * 10) / 10 : Math.floor(v)}${s}`;
}
